import type { FC } from 'react'
import type { Theme } from '@mui/material'
import { Paper, Box, Typography, Chip, Checkbox } from '@mui/material'
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable'
import type { DragEndEvent } from '@dnd-kit/core'
import { PackageRewardCard } from './PackageRewardCard'

interface RewardItem {
  rewardId: string
  rewardName: string
  supportsEmployeeContribution: boolean
  selected: boolean
  companyContribution: number
  employeeContribution: number
  rewardDisplayOrder: number
}

interface PackageCategoryCardProps {
  categoryName: string
  rewards: RewardItem[]
  onToggleRewardSelect: (id: string) => void
  onContributionChange: (id: string, field: 'companyContribution' | 'employeeContribution', value: string) => void
  onReorderRewards?: (categoryName: string, oldIndex: number, newIndex: number) => void
  formatCurrency: (value: number) => string
  theme: Theme
  // Optional Select All props (only for first category)
  showSelectAll?: boolean
  allSelected?: boolean
  someSelected?: boolean
  onToggleAll?: (checked: boolean) => void
  // Disable drag and drop functionality
  disableDragAndDrop?: boolean
  // Hide selection checkboxes
  hideSelection?: boolean
}

export const PackageCategoryCard: FC<PackageCategoryCardProps> = ({
  categoryName,
  rewards,
  onToggleRewardSelect,
  onContributionChange,
  onReorderRewards,
  formatCurrency,
  theme,
  showSelectAll = false,
  allSelected = false,
  someSelected = false,
  onToggleAll,
  disableDragAndDrop = false,
  hideSelection = false,
}) => {
  const selectedCount = rewards.filter((r) => r.selected).length

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id && onReorderRewards && !disableDragAndDrop) {
      const oldIndex = rewards.findIndex((r) => r.rewardId === active.id)
      const newIndex = rewards.findIndex((r) => r.rewardId === over.id)
      onReorderRewards(categoryName, oldIndex, newIndex)
    }
  }

  return (
    <Paper
      elevation={2}
      sx={{
        mb: 1.5,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* Category Header */}
      <Box
        sx={{
          px: 1.5,
          py: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          backgroundColor: theme.palette.mode === 'dark' ? theme.palette.action.hover : theme.palette.grey[100],
        }}
      >
        <Typography variant="subtitle1" fontWeight={600} sx={{ flexGrow: 1, fontSize: '0.95rem' }}>
          {categoryName}
        </Typography>

        {!hideSelection && (
          <Chip label={`${selectedCount}/${rewards.length}`} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
        )}
        {hideSelection && <Chip label={`${rewards.length}`} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />}
      </Box>

      {/* Column Headers */}
      <Box
        sx={{
          px: 2.5,
          py: 0.75,
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[50] : theme.palette.grey[50],
        }}
      >
        {/* Drag handle spacer - only show if drag and drop is enabled */}
        {!disableDragAndDrop && <Box sx={{ width: 16 }} />}

        {/* Select All checkbox (only for first category) or spacer */}
        {!hideSelection && (
          <>
            {showSelectAll ? (
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected}
                onChange={(e) => onToggleAll?.(e.target.checked)}
                size="small"
              />
            ) : (
              <Box sx={{ width: 42 }} />
            )}
          </>
        )}

        <Box sx={{ flexGrow: 1, minWidth: 120 }}>
          <Typography variant="caption" fontWeight={600} color="textSecondary">
            {!hideSelection && showSelectAll ? 'Select All / Reward' : 'Reward'}
          </Typography>
        </Box>

        <Box sx={{ width: 140, textAlign: 'center' }}>
          <Typography variant="caption" fontWeight={600} color="textSecondary">
            Company
          </Typography>
        </Box>

        <Box sx={{ width: 140, textAlign: 'center' }}>
          <Typography variant="caption" fontWeight={600} color="textSecondary">
            Employee
          </Typography>
        </Box>
      </Box>

      {/* Rewards List */}
      <Box sx={{ p: 1 }}>
        {rewards.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2 }}>
            <Typography color="textSecondary" variant="caption">
              No rewards in this category
            </Typography>
          </Box>
        ) : disableDragAndDrop ? (
          // Render without drag and drop context when disabled
          <>
            {rewards.map((reward) => (
              <PackageRewardCard
                key={reward.rewardId}
                rewardId={reward.rewardId}
                rewardName={reward.rewardName}
                supportsEmployeeContribution={reward.supportsEmployeeContribution}
                isSelected={reward.selected}
                companyContribution={reward.companyContribution}
                employeeContribution={reward.employeeContribution}
                onToggleSelect={onToggleRewardSelect}
                onContributionChange={onContributionChange}
                formatCurrency={formatCurrency}
                disableDragAndDrop={disableDragAndDrop}
                hideSelection={hideSelection}
              />
            ))}
          </>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={rewards.map((r) => r.rewardId)} strategy={verticalListSortingStrategy}>
              {rewards.map((reward) => (
                <PackageRewardCard
                  key={reward.rewardId}
                  rewardId={reward.rewardId}
                  rewardName={reward.rewardName}
                  supportsEmployeeContribution={reward.supportsEmployeeContribution}
                  isSelected={reward.selected}
                  companyContribution={reward.companyContribution}
                  employeeContribution={reward.employeeContribution}
                  onToggleSelect={onToggleRewardSelect}
                  onContributionChange={onContributionChange}
                  formatCurrency={formatCurrency}
                  disableDragAndDrop={disableDragAndDrop}
                  hideSelection={hideSelection}
                />
              ))}
            </SortableContext>
          </DndContext>
        )}
      </Box>
    </Paper>
  )
}
